import { SUBJECTS_REG_ENDPOINTS } from "../constants/api";
import { getErrorMessage, privateApiClient } from "../utils/axios.utils";
import {
  SubjectRegistration,
  SubjectRegistrationCollection,
} from "../types/types";

interface CreateSubjectRegistrationData {
  exam_registration_id: number;
  subject_id: number;
  exam_category_id: number;
  student_count: number;
}

interface UpdateSubjectRegistrationData {
  id?: number; // Optional for create, required for update
  student_count: number;
  exam_category_id?: number;
}

export class SubjectRegistrationService {
  /**
   * Fetch all subject registrations, optionally filtered by exam_registration_id.
   */
  async getSubjectRegistrations(
    examRegistrationId?: number
  ): Promise<SubjectRegistrationCollection> {
    try {
      const response = await privateApiClient.get<{
        data: SubjectRegistrationCollection;
        message: string;
        success: boolean;
      }>(SUBJECTS_REG_ENDPOINTS.GET_ALL, {
        params: { exam_registration_id: examRegistrationId },
      });
      return response.data.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Fetch own subject registrations, optionally filtered by exam_registration_id, with pagination support.
   */
  async getOwnSubjectRegistrations(
    examRegistrationId?: number,
    limit: number = 10,
    page: number = 1
  ): Promise<SubjectRegistrationCollection> {
    try {
      const response = await privateApiClient.get<{
        data: SubjectRegistrationCollection;
        message: string;
        success: boolean;
      }>(SUBJECTS_REG_ENDPOINTS.GET_OWN_SCHOOL, {
        params: { exam_registration_id: examRegistrationId, limit, page },
      });
      return response.data.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Fetch a single subject registration by ID.
   */
  async getSubjectRegistration(id: number): Promise<SubjectRegistration> {
    try {
      const response = await privateApiClient.get<{
        data: SubjectRegistration;
        message: string;
        success: boolean;
      }>(SUBJECTS_REG_ENDPOINTS.GET(id));
      return response.data.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Create a single subject registration.
   */
  //   async createSubjectRegistration(
  //     data: CreateSubjectRegistrationData
  //   ): Promise<SubjectRegistration> {
  //     try {
  //       const response = await privateApiClient.post<{
  //         data: SubjectRegistration;
  //         message: string;
  //         success: boolean;
  //       }>("/subject-registrations", { registrations: [data] });
  //       return response.data; // Extract first item from collection
  //     } catch (error) {
  //       throw new Error(getErrorMessage(error));
  //     }
  //   }

  /**
   * Create multiple subject registrations in a batch.
   */
  async createSubjectRegistrations(
    data: CreateSubjectRegistrationData[]
  ): Promise<SubjectRegistrationCollection> {
    try {
      const response = await privateApiClient.post<{
        data: SubjectRegistrationCollection;
        message: string;
        success: boolean;
      }>(SUBJECTS_REG_ENDPOINTS.CREATE_BATCH, { registrations: data });
      return response.data.data;
    } catch (error) {
      // console.log("Subject Registration Error Create Batch", error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Update a single subject registration.
   */
  async updateSubjectRegistration(
    id: number,
    data: UpdateSubjectRegistrationData
  ): Promise<SubjectRegistration> {
    try {
      const response = await privateApiClient.put<{
        data: SubjectRegistration;
        message: string;
        success: boolean;
      }>(`/subject-registrations/${id}`, {
        registrations: [{ id, student_count: data.student_count }],
      });
      return response.data.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Update multiple subject registrations in a batch.
   */
  async updateSubjectRegistrations(
    data: UpdateSubjectRegistrationData[]
  ): Promise<SubjectRegistrationCollection> {
    try {
      const response = await privateApiClient.put<{
        data: SubjectRegistrationCollection;
        message: string;
        success: boolean;
      }>("/subject-registrations", { registrations: data });
      return response.data.data;
    } catch (error) {
      // console.log("Subject Registration Error", error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Delete a subject registration by ID.
   */
  async deleteSubjectRegistration(id: number): Promise<void> {
    try {
      await privateApiClient.delete(`/subject-registrations/${id}`);
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Delete multiple subject registrations by IDs.
   */
  async deleteSubjectRegistrations(ids: number[]): Promise<void> {
    try {
      await privateApiClient.delete("/subject-registrations", {
        data: { ids },
      });
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  }
}
